import { QBankStructureLevelCategory } from '@exam-management/qbs-provider';
import {
  LocationFormFields,
  type LocationFormFieldsProps,
} from '@exam-management/qcontent-management';
import {
  Button,
  Collapse,
  ExpandMoreRoundedIcon,
  FormRadioSelection,
  Text,
  Flex,
  Rotate,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useFormContext } from 'react-hook-form';

import { QuestionCategoryInput } from '../../constants';
import { useQContentSearchParams } from '../../hooks';

const CATEGORY_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Single & LOQ', value: QuestionCategoryInput.SINGLE_AND_LOQ },
  { label: 'Item Set', value: QuestionCategoryInput.ITEM_SET },
];

interface QContentImportFormProps {
  loading?: boolean;
  qbsLevels: LocationFormFieldsProps['qbsLevels'];
  qbsLevelItems: LocationFormFieldsProps['qbsLevelItems'];
}

export default function QContentListFilterForm(props: Readonly<QContentImportFormProps>) {
  const { loading: isLoading, qbsLevels, qbsLevelItems } = props;
  const [{ part, subject, group, chapter, topic, subtopic }] = useQContentSearchParams();
  const hasLocationFilter = Boolean(part || subject || group || chapter || topic || subtopic);
  // NOTE: type is not assigned here because circular dependency
  const formContext = useFormContext();

  const [isLocationFilterOpen, locationFilterOpenHandlers] = useDisclosure(hasLocationFilter);
  const { toggle: toggleLocationFilterOpen } = locationFilterOpenHandlers;

  const handleFormReset = () => {
    const { category, qIds } = formContext.getValues();

    formContext.reset({
      location: {
        [QBankStructureLevelCategory.Part]: null,
        [QBankStructureLevelCategory.Subject]: null,
        [QBankStructureLevelCategory.Group]: null,
        [QBankStructureLevelCategory.Chapter]: null,
        [QBankStructureLevelCategory.Topic]: null,
        [QBankStructureLevelCategory.SubTopic]: null,
      },
      category: category,
      qIds: qIds,
    });
  };

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
      <FormRadioSelection
        name='category'
        disabled={isLoading}
        options={CATEGORY_OPTIONS}
        label='Question Type'
        row
        sx={{
          '& .MuiFormControlLabel-root': {
            mr: 4,
          },
        }}
      />
      <Button
        color='secondary'
        size='large'
        variant='outlined'
        sx={{ width: 1 }}
        onClick={toggleLocationFilterOpen}
      >
        Filter by Location{' '}
        <Rotate in={isLocationFilterOpen}>
          <ExpandMoreRoundedIcon sx={{ ml: (theme) => theme.spacing(2) }} />
        </Rotate>
      </Button>
      <Collapse in={isLocationFilterOpen}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <Flex sx={{ justifyContent: 'space-between' }}>
            <Text variant='h4'>Location</Text>
            <Button
              color='secondary'
              size='small'
              variant='outlined'
              sx={{ minWidth: 'unset' }}
              onClick={handleFormReset}
            >
              Reset
            </Button>
          </Flex>
          <LocationFormFields
            prefix='location'
            qbsLevels={qbsLevels}
            qbsLevelItems={qbsLevelItems}
            allowSkipLevel
          />
        </Flex>
      </Collapse>
    </Flex>
  );
}
